import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  competitionClassifications,
  competitionRegistrationModes,
  type AthleteView,
  type CompetitionRegistrationView,
  type CompetitionSeasonView,
  type PageResponse,
  type SupportedLocale,
  type TeamView,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  activeFirst,
  classificationOf,
  correctionFormOf,
  correctionRequest,
  emptyRegistrationForm,
  endRequest,
  registrableTeams,
  registrationClassificationKeys,
  registrationModeKeys,
  registrationRequest,
  registrationsPath,
  registrationStatusKeys,
  type EndRegistrationForm,
  type RegistrationCorrectionForm,
  type RegistrationForm,
} from './competitionRegistrations';
import { latestPlausibleDay } from './competitions';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';

export interface RegistrationsSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  competition: CompetitionSeasonView;
}

type OpenPanel =
  | { kind: 'correct'; registrationId: string; form: RegistrationCorrectionForm }
  | { kind: 'end'; registrationId: string; form: EndRegistrationForm };

/**
 * Athletes registered in one competition season. A registrar creates a registration through a team
 * of the competition's department and season, corrects its end day or licence reference, or ends it;
 * ended registrations stay as history. Registration is separate from club membership and rosters.
 */
export function RegistrationsSection({
  locale,
  base,
  csrfToken,
  competition,
}: RegistrationsSectionProps): React.JSX.Element {
  const [registrations, setRegistrations] = useState<CompetitionRegistrationView[] | null>(null);
  const [teams, setTeams] = useState<TeamView[]>([]);
  const [form, setForm] = useState<RegistrationForm>(emptyRegistrationForm);
  const [formKey, setFormKey] = useState(0);
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);

  const load = useCallback(async () => {
    try {
      setRegistrations(
        await apiRequest<CompetitionRegistrationView[]>(
          `${registrationsPath(base)}?competitionSeasonId=${encodeURIComponent(competition.id)}`,
        ),
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, competition.id]);

  useEffect(() => {
    setRegistrations(null);
    setPanel(null);
    setErrorKey(null);
    setMessage(null);
    setForm(emptyRegistrationForm);
    setFormKey((current) => current + 1);
    void load();
  }, [load]);

  useEffect(() => {
    if (!competition.canManage) return;
    void apiRequest<TeamView[]>(
      `${base}/teams?seasonId=${encodeURIComponent(competition.seasonId)}`,
    )
      .then((loaded) => {
        setTeams(registrableTeams(loaded, competition));
      })
      .catch((error: unknown) => {
        setErrorKey(errorKeyOf(error));
      });
  }, [base, competition]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function submit(action: () => Promise<unknown>, successKey: TranslationKey): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(successKey);
      setPanel(null);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // A version conflict or an ended registration: show what the server holds now.
      await load();
    }
  }

  const createRequest = registrationRequest(form, competition.id);

  async function register(): Promise<void> {
    if (createRequest === null) return;
    await submit(async () => {
      await apiRequest<CompetitionRegistrationView>(registrationsPath(base), {
        method: 'POST',
        body: createRequest,
        csrfToken,
      });
      setForm(emptyRegistrationForm);
      setFormKey((current) => current + 1);
    }, 'registrations.done');
  }

  async function correct(
    registration: CompetitionRegistrationView,
    request: NonNullable<ReturnType<typeof correctionRequest>>,
  ): Promise<void> {
    await submit(async () => {
      await apiRequest<CompetitionRegistrationView>(registrationsPath(base, registration.id), {
        method: 'PATCH',
        body: request,
        csrfToken,
        ifMatchVersion: registration.version,
      });
    }, 'registrations.correct.done');
  }

  async function end(
    registration: CompetitionRegistrationView,
    request: NonNullable<ReturnType<typeof endRequest>>,
  ): Promise<void> {
    await submit(async () => {
      await apiRequest<CompetitionRegistrationView>(
        registrationsPath(base, registration.id, 'end'),
        { method: 'POST', body: request, csrfToken, ifMatchVersion: registration.version },
      );
    }, 'registrations.end.done');
  }

  const noTeams = competition.canManage && teams.length === 0;

  const classificationOptions = competitionClassifications.map((classification) => (
    <MenuItem key={classification} value={classification}>
      {translate(locale, registrationClassificationKeys[classification])}
    </MenuItem>
  ));

  return (
    <Box component="section" aria-labelledby="registrations-title" sx={{ mt: 3 }}>
      <Typography variant="h6" component="h3" id="registrations-title" gutterBottom>
        {translate(locale, 'registrations.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {registrations === null && errorKey === null && (
        <Typography>{translate(locale, 'registrations.loading')}</Typography>
      )}
      {registrations?.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'registrations.empty')}
        </Typography>
      )}
      <Stack component="ul" spacing={2} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 3 }}>
        {activeFirst(registrations ?? []).map((registration) => {
          const open = panel?.registrationId === registration.id ? panel : null;
          const correction =
            open?.kind === 'correct' ? correctionRequest(open.form, registration) : null;
          const ending =
            open?.kind === 'end' ? endRequest(open.form, registration, latestPlausibleDay()) : null;
          return (
            <Box component="li" key={registration.id}>
              <Typography variant="subtitle2">
                {athleteDisplayName(registration.athlete, locale)} · {registration.team.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(locale, registrationModeKeys[registration.mode])} ·{' '}
                {translate(locale, registrationStatusKeys[registration.status])} ·{' '}
                {formatCalendarDate(registration.validFrom, locale)}
                {' – '}
                {registration.validTo === null
                  ? translate(locale, 'registrations.openEnded')
                  : formatCalendarDate(registration.validTo, locale)}
                {registration.classification === 'general'
                  ? ''
                  : ` · ${translate(locale, registrationClassificationKeys[registration.classification])}`}
                {registration.licenseReference === null
                  ? ''
                  : ` · ${translate(locale, 'registrations.license')}: ${registration.licenseReference}`}
              </Typography>
              {registration.endedReason !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'registrations.endedReason')}: {registration.endedReason}
                </Typography>
              )}
              {registration.canManage && registration.status === 'active' && open === null && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({
                        kind: 'correct',
                        registrationId: registration.id,
                        form: correctionFormOf(registration),
                      });
                    }}
                  >
                    {translate(locale, 'registrations.correct')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({
                        kind: 'end',
                        registrationId: registration.id,
                        form: { endedOn: '', reason: '' },
                      });
                    }}
                  >
                    {translate(locale, 'registrations.end')}
                  </Button>
                </Box>
              )}
              {open?.kind === 'correct' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (correction !== null) void correct(registration, correction);
                  }}
                >
                  <TextField
                    size="small"
                    type="date"
                    label={translate(locale, 'registrations.field.validTo')}
                    value={open.form.validTo}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, validTo: event.target.value } });
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    size="small"
                    label={translate(locale, 'registrations.field.license')}
                    value={open.form.licenseReference}
                    onChange={(event) => {
                      setPanel({
                        ...open,
                        form: { ...open.form, licenseReference: event.target.value },
                      });
                    }}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                  />
                  <TextField
                    select
                    size="small"
                    label={translate(locale, 'registrations.field.classification')}
                    value={open.form.classification}
                    onChange={(event) => {
                      const classification = classificationOf(event.target.value);
                      if (classification !== undefined) {
                        setPanel({ ...open, form: { ...open.form, classification } });
                      }
                    }}
                  >
                    {classificationOptions}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={correction === null}
                    >
                      {translate(locale, 'registrations.correct.submit')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setPanel(null);
                      }}
                    >
                      {translate(locale, 'registrations.cancel')}
                    </Button>
                  </Box>
                </Stack>
              )}
              {open?.kind === 'end' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (ending !== null) void end(registration, ending);
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'registrations.end.hint')}
                  </Typography>
                  <TextField
                    required
                    size="small"
                    type="date"
                    label={translate(locale, 'registrations.end.day')}
                    value={open.form.endedOn}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, endedOn: event.target.value } });
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'registrations.end.reason')}
                    value={open.form.reason}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, reason: event.target.value } });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={ending === null}
                    >
                      {translate(locale, 'registrations.end.submit')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setPanel(null);
                      }}
                    >
                      {translate(locale, 'registrations.cancel')}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {competition.canManage && (
        <Stack
          component="form"
          key={formKey}
          spacing={2}
          sx={{ maxWidth: 480 }}
          onSubmit={(event) => {
            event.preventDefault();
            void register();
          }}
        >
          <Typography variant="subtitle1" component="h4">
            {translate(locale, 'registrations.create.title')}
          </Typography>
          {noTeams && <Alert severity="info">{translate(locale, 'registrations.noTeams')}</Alert>}
          <RemoteSelect
            label={translate(locale, 'registrations.field.athlete')}
            noMatchesText={translate(locale, 'registrations.noAthletes')}
            load={findAthletes}
            onSelect={(athleteId) => {
              setForm((current) => ({ ...current, athleteId }));
            }}
          />
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'registrations.field.team')}
            value={form.teamId}
            onChange={(event) => {
              setForm({ ...form, teamId: event.target.value });
            }}
          >
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'registrations.field.mode')}
            value={form.mode}
            helperText={translate(locale, 'registrations.mode.hint')}
            onChange={(event) => {
              const mode = competitionRegistrationModes.find(
                (candidate) => candidate === event.target.value,
              );
              if (mode !== undefined) setForm({ ...form, mode });
            }}
          >
            {competitionRegistrationModes.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {translate(locale, registrationModeKeys[mode])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'registrations.field.classification')}
            value={form.classification}
            helperText={translate(locale, 'registrations.classification.hint')}
            onChange={(event) => {
              const classification = classificationOf(event.target.value);
              if (classification !== undefined) setForm({ ...form, classification });
            }}
          >
            {classificationOptions}
          </TextField>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'registrations.field.validFrom')}
            value={form.validFrom}
            onChange={(event) => {
              setForm({ ...form, validFrom: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'registrations.field.validTo')}
            value={form.validTo}
            onChange={(event) => {
              setForm({ ...form, validTo: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            label={translate(locale, 'registrations.field.license')}
            value={form.licenseReference}
            onChange={(event) => {
              setForm({ ...form, licenseReference: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={createRequest === null}>
              {translate(locale, 'registrations.submit')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
